import React, { useState } from 'react';
import { FaTelegram, FaGift, FaClock, FaDollarSign, FaBookReader, FaChartLine, FaHeadset, FaTicketAlt } from 'react-icons/fa';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext'; // Предполагаю, что у вас есть контекст авторизации
import BottomNav from '../components/mobile/BottomNav';
import { useTranslations } from '../hooks/useTranslations';

function Bonuses() {
  const [showOverdraftSuccess, setShowOverdraftSuccess] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingCode, setIsApplyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeSuccess, setCodeSuccess] = useState('');
  const { data, refreshUserData } = useAuth(); // Получаем данные пользователя из контекста
  const { t } = useTranslations();

  const handleOverdraftClick = async () => {
    try {
      const response = await fetch(`https://api.maxprofitacademy.com/user/overdraft/${data?.data.user_id}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setShowOverdraftSuccess(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      // Обновляем данные пользователя после успешной активации
      await refreshUserData();

    } catch (error) {
      console.error('Error activating overdraft:', error);
    }
  };

  // Функция для активации промо-кода
  const handleApplyPromoCode = async (e) => {
    e.preventDefault();
    
    if (!promoCode.trim()) {
      setCodeError(t('settings.emptyCodeError') || 'Введите промо-код');
      return;
    }

    setIsApplyingCode(true);
    setCodeError('');
    setCodeSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCodeError(t('settings.notAuthorized') || 'Необходима авторизация');
        return;
      }

      const response = await fetch('https://api.maxprofitacademy.com/user/apply-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'token': JSON.parse(token)
        },
        body: JSON.stringify({
          code: promoCode.trim().toUpperCase()
        })
      });

      const responseData = await response.json();

      if (response.ok && responseData.status === 200) {
        setCodeSuccess(t('settings.codeAppliedSuccess') || 'Промо-код успешно активирован!');
        setPromoCode('');
        // Обновляем данные пользователя
        await refreshUserData();
        
        // Запускаем конфетти
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        setTimeout(() => setCodeSuccess(''), 5000);
      } else {
        setCodeError(responseData.message || t('settings.codeApplyError') || 'Ошибка при активации кода');
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
      setCodeError(t('settings.serverError') || 'Ошибка сервера');
    } finally {
      setIsApplyingCode(false);
    }
  };

  // Если overdraft уже активирован, не показываем карточку overdraft
  const showOverdraftCard = data?.data?.overdraft === false;

  return (
    <div className="space-y-8">
      {/* Header Section */}
      {/* <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          Специальные предложения для вас
        </h1>
        <p className="text-lg text-gray-600">
          Получите доступ к эксклюзивным возможностям и бонусам
        </p>
      </div> */}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Promo Code Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl md:p-8 p-6 text-white shadow-xl transform hover:scale-105 transition-transform duration-300">
          <div className="flex items-center mb-6">
            <FaTicketAlt className="w-12 h-12" />
            <h2 className="text-xl md:text-2xl font-bold ml-4">{t('settings.promoCode') || 'Промо-код'}</h2>
          </div>
          <p className="mb-6 text-sm md:text-base">
            {t('settings.promoCodeDescription') || 'Если у вас есть промо-код, активируйте его для получения специальных цен'}
          </p>
          
          {data?.data?.code && (
            <div className="p-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg mb-4">
              <p className="text-sm">
                {t('settings.activeCode') || 'Активный код'}: <span className="font-bold text-lg">{data.data.code}</span>
              </p>
            </div>
          )}
          
          <form onSubmit={handleApplyPromoCode} className="space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder={t('settings.enterPromoCode') || 'Введите промо-код'}
                className="flex-1 px-4 py-2 text-sm rounded-lg border-0 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white uppercase"
                disabled={isApplyingCode}
              />
              <button
                type="submit"
                disabled={isApplyingCode || !promoCode.trim()}
                className="px-4 py-2 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap transition-colors"
              >
                {isApplyingCode ? (t('settings.applying') || 'Применение...') : (t('settings.apply') || 'Применить')}
              </button>
            </div>
            
            {codeError && (
              <div className="p-2 bg-red-100 border border-red-300 text-red-800 rounded-lg text-xs">
                {codeError}
              </div>
            )}
            
            {codeSuccess && (
              <div className="p-2 bg-green-100 border border-green-300 text-green-800 rounded-lg text-xs">
                {codeSuccess}
              </div>
            )}
          </form>
        </div>

        {/* Telegram Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl md:p-8 p-6 text-white shadow-xl transform hover:scale-105 transition-transform duration-300">
          <div className="flex items-center mb-6">
            <FaTelegram className="w-12 h-12" />
            <h2 className="text-xl md:text-2xl font-bold ml-4">{t('bonuses.telegramChannel')}</h2>
          </div>
          <p className="mb-8 text-sm md:text-lg">
            {t('bonuses.telegramDescription')}
          </p>
          <a 
            href="https://t.me/MaxProfitAcademyPro" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center md:justify-start justify-center md:w-auto w-full px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300"
          >
            <FaTelegram className="mr-2" />
            {t('bonuses.join')}
          </a>
        </div>

        {/* Overdraft Card - показываем только если overdraft === false */}
        {showOverdraftCard && (
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl md:p-8 p-6 text-white shadow-xl transform hover:scale-105 transition-transform duration-300">
            <div className="flex items-center mb-6">
              <FaGift className="w-12 h-12" />
              <h2 className="md:text-2xl text-xl font-bold ml-4">{t('bonuses.overdraft')}</h2>
            </div>
            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <FaDollarSign className="w-6 h-6 md:mr-3 mr-1" />
                <p className="md:text-lg text-sm">{t('bonuses.tradingDeposit')}</p>
              </div>
              <div className="flex items-center">
                <FaClock className="w-6 h-6 md:mr-3 mr-1" />
                <p className="md:text-lg text-sm">{t('bonuses.freeAccess')}</p>
              </div>
            </div>
            {!showOverdraftSuccess ? (
              <button
                onClick={handleOverdraftClick}
                className="inline-flex md:justify-start justify-center items-center w-full md:w-auto md:text-base px-6 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-emerald-50 transition-colors duration-300"
              >
                <FaGift className="mr-2" />
                {t('bonuses.activate')}
              </button>
            ) : (
              <div className="bg-white text-emerald-600 rounded-lg p-4 text-center font-semibold animate-bounce">
                {t('bonuses.congratulations')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Additional Benefits Section */}
      <div className="mt-12 md:bg-gray-50 md:rounded-2xl md:p-8">
        <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
          {t('bonuses.participationBenefits')}
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="bg-primary w-12 h-12 rounded-full flex items-center justify-center">
                <FaBookReader className="h-6 w-6 text-[#277B6F]" />
              </div>
              <h4 className="font-semibold md:text-lg text-sm ml-3">{t('bonuses.exclusiveContent')}</h4>
            </div>
            <p className="text-gray-600 md:text-sm text-xs">{t('bonuses.exclusiveContentDescription')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="bg-primary w-12 h-12 rounded-full flex items-center justify-center">
                <FaChartLine className="h-6 w-6 text-[#277B6F]" />
              </div>
              <h4 className="font-semibold md:text-lg text-sm ml-3">{t('bonuses.tradingSignals')}</h4>
            </div>
            <p className="text-gray-600 md:text-sm text-xs">{t('bonuses.tradingSignalsDescription')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="bg-primary w-12 h-12 rounded-full flex items-center justify-center">
                <FaHeadset className="h-6 w-6 text-[#277B6F]" />
              </div>
              <h4 className="font-semibold md:text-lg text-sm ml-3">{t('bonuses.support247')}</h4>
            </div>
            <p className="text-gray-600 md:text-sm text-xs">{t('bonuses.support247Description')}</p>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

export default Bonuses; 